import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { expenseService } from "@/services/expense";
import type { ExpenseListParams } from "@/services/expense";

import type {
  CreateExpenseReq,
  UpdateExpenseReq,
} from "@artisancode/api-types";

export function useExpenses(params?: ExpenseListParams) {
  return useQuery({
    queryKey: queryKeys.expenses.list(params as Record<string, unknown>),
    queryFn: () => expenseService.list(params),
  });
}

export function useExpense(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.expenses.detail(id ?? ""),
    queryFn: () => expenseService.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateExpenseReq;
      idempotencyKey?: string;
    }) => expenseService.create(body, idempotencyKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.expenses.all }),
  });
}

export function useCompleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      args: string | { id: string; acknowledgeCostImpact?: boolean },
    ) => {
      const id = typeof args === "string" ? args : args.id;
      const acknowledgeCostImpact =
        typeof args === "string" ? undefined : args.acknowledgeCostImpact;
      return expenseService.complete(id, { acknowledgeCostImpact });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.expenses.all }),
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateExpenseReq) =>
      expenseService.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.expenses.all }),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      expenseService.delete(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.expenses.all }),
  });
}
